import { DELIVERY_OPTION_CODES, getDeliveryOptionCode } from './deliveryOptions';

export const SINGPOST_ADDRESS_LINE_LIMIT = 35;
export const SINGPOST_COMPANY_LINE_LIMIT = 35;
export const SINGPOST_COMPANY_WARNING =
  'SingPost labels allow 35 characters for company/business name. Please shorten the company field.';

const normalizeSpaces = (value) => String(value || '').replace(/\s+/g, ' ').trim();

export const normalizeSingaporePostcode = (postcode) => {
  const raw = normalizeSpaces(postcode);
  if (!raw) return '';
  const digits = raw.match(/\d/g)?.join('') || '';
  return digits.length === 6 ? digits : raw;
};

export const isSingaporeCountry = (country) => {
  const normalized = normalizeSpaces(country).toLowerCase();
  return normalized === 'singapore' || normalized === 'sg';
};

export const isSingPostTrackedLetterboxOption = (deliveryOption) => (
  getDeliveryOptionCode(deliveryOption) === DELIVERY_OPTION_CODES.SINGPOST_TRACKED_LETTERBOX
);

const splitAddressLineOnComma = (address1, address2) => {
  const line1 = normalizeSpaces(address1);
  const line2 = normalizeSpaces(address2);
  if (!line1 || line2 || line1.length <= SINGPOST_ADDRESS_LINE_LIMIT || !line1.includes(',')) {
    return { address1: line1, address2: line2, didSplit: false };
  }

  const parts = line1.split(',').map(normalizeSpaces).filter(Boolean);
  if (parts.length < 2) return { address1: line1, address2: line2, didSplit: false };

  const first = parts[0];
  const second = parts.slice(1).join(', ');
  if (
    first.length <= SINGPOST_ADDRESS_LINE_LIMIT &&
    second.length <= SINGPOST_ADDRESS_LINE_LIMIT
  ) {
    return { address1: first, address2: second, didSplit: true };
  }

  return { address1: line1, address2: line2, didSplit: false };
};

export const normalizeDeliveryAddress = (address = {}) => {
  const split = splitAddressLineOnComma(address.address1, address.address2);
  const country = normalizeSpaces(address.country);
  return {
    address: {
      ...address,
      address1: split.address1,
      address2: split.address2,
      locality: normalizeSpaces(address.locality) || (isSingaporeCountry(country) ? 'Singapore' : ''),
      state: normalizeSpaces(address.state),
      postcode: normalizeSingaporePostcode(address.postcode),
      country,
    },
    didSplit: split.didSplit,
  };
};

export const validateSingPostTrackedAddress = (deliveryOption, address = {}, user = {}) => {
  const { address: normalized, didSplit } = normalizeDeliveryAddress(address);
  const company = normalizeSpaces(user.company);
  if (!isSingPostTrackedLetterboxOption(deliveryOption) || !isSingaporeCountry(normalized.country)) {
    return { address: normalized, warnings: [], didSplit };
  }

  const warnings = [];
  if (!/^\d{6}$/.test(normalized.postcode)) {
    warnings.push('Singapore postal code must be 6 digits for SingPost Tracked Letterbox.');
  }
  if (normalized.address1.length > SINGPOST_ADDRESS_LINE_LIMIT) {
    warnings.push('SingPost labels allow 35 characters per address line. Please move part of this address to line 2.');
  }
  if (normalized.address2.length > SINGPOST_ADDRESS_LINE_LIMIT) {
    warnings.push('SingPost labels allow 35 characters per address line. Please shorten address line 2.');
  }
  if (company.length > SINGPOST_COMPANY_LINE_LIMIT) {
    warnings.push(SINGPOST_COMPANY_WARNING);
  }

  return { address: normalized, warnings, didSplit };
};
